/**
 * Main-thread ambience logic (race audio 2, P4): the music intensity from race data, and the swell / sting
 * schedule from events.json times (race time, so the warp and every mode are covered). Pure, no Web Audio.
 */
import type { RaceEvent } from '../api.ts';

export const GAP_FAR_M = 250;         // a battle pair further apart than this adds nothing
export const LAST_LAPS = 5;
export const BOOST_TAU_S = 8;         // presentation seconds; the event age is divided by the playback rate
const SEEK_RACE_S = 20;               // a race-time step bigger than this is a seek: no triggers

export interface IntensityInput {
  /** distance between the battle pair in metres, null = no pair on show */
  gapM: number | null;
  lap: number;
  laps: number;
  /** decaying event boost, 0..1 (EventSchedule.boost) */
  boost: number;
}

/** 0.2 base + up to 0.35 from a close battle pair + up to 0.25 over the last 5 laps + event boost, clamped to 0..1 */
export function intensityOf(i: IntensityInput): number {
  const gap = i.gapM === null ? 0 : Math.min(Math.max(1 - i.gapM / GAP_FAR_M, 0), 1);
  const left = Math.max(i.laps - i.lap, 0);
  const late = Math.min(Math.max((LAST_LAPS - left) / LAST_LAPS, 0), 1);
  return Math.min(Math.max(0.2 + 0.35 * gap + 0.25 * late + i.boost, 0), 1);
}

interface Mark { t: number; w: number; sting: boolean }

/** crowd swell strength and intensity boost per event kind */
export function marksOf(events: readonly RaceEvent[]): Mark[] {
  const out: Mark[] = [];
  let finish = false;
  for (const e of events) {
    if (e.type === 'start') out.push({ t: e.t, w: 0.8, sting: false });
    else if (e.type === 'leader_change') out.push({ t: e.t, w: 0.7, sting: false });
    else if (e.type === 'overtake' && e.top10) out.push({ t: e.t, w: 0.4, sting: false });
    else if (e.type === 'chequered_flag' && !finish) { finish = true; out.push({ t: e.t, w: 1, sting: true }); }
  }
  return out.sort((a, b) => a.t - b.t);
}

export class EventSchedule {
  private readonly marks: Mark[];
  private last = Number.NaN;
  /** index of the first mark not yet passed */
  private next = 0;

  constructor(events: readonly RaceEvent[]) { this.marks = marksOf(events); }

  private rewind(raceT: number): void {
    this.next = this.marks.findIndex((m) => m.t >= raceT);
    if (this.next < 0) this.next = this.marks.length;
  }

  /** Marks crossed going forward since the last call: summed swell (<= 1) and whether the finish sting is among them. */
  step(raceT: number): { swell: number; sting: boolean } {
    const a = this.last;
    this.last = raceT;
    if (Number.isNaN(a) || raceT < a || raceT - a > SEEK_RACE_S) { this.rewind(raceT); return { swell: 0, sting: false }; }
    if (raceT === a) return { swell: 0, sting: false };            // a hold: the start fires on the first move after it
    let swell = 0, sting = false;
    for (; this.next < this.marks.length; this.next++) {
      const m = this.marks[this.next] as Mark;
      if (m.t > raceT) break;
      swell += m.w; sting ||= m.sting;
    }
    return { swell: Math.min(swell, 1), sting };
  }

  /** decaying boost from marks in the recent past: weight * exp(-age / (tau * rate)), 0..1 */
  boost(raceT: number, rate: number): number {
    const tau = BOOST_TAU_S * Math.max(rate, 1);
    let b = 0;
    for (const m of this.marks) {
      if (m.t > raceT) break;
      const age = raceT - m.t;
      if (age < 4 * tau) b += 0.3 * m.w * Math.exp(-age / tau);
    }
    return Math.min(b, 0.5);
  }
}

/** crowd level follows the camera zoom slightly: 0.7 zoomed far out (0.1 px/m) up to 1.0 zoomed in (10 px/m) */
export function crowdZoomGain(zoom: number): number {
  const x = (Math.log10(Math.max(zoom, 1e-6)) + 1) / 2;
  return 0.7 + 0.3 * Math.min(Math.max(x, 0), 1);
}
